/**
 * <swiper-grid-carousel-latori> pages through a fixed multi-row grid of
 * slides per view (Swiper's Grid module), e.g. 2 rows x N columns per page,
 * with "{page} {current} {of} {total}" fraction pagination instead of dots.
 *
 * Data attributes (all optional, read from the host element):
 * - data-grid-rows   Rows per page (default 1)
 * - data-breakpoints JSON map of {minWidth: columnsPerPage}, e.g.
 *                    '{"0":2,"768":3,"1204":4,"1280":6}' - the lowest key is
 *                    the mobile-first base slidesPerView, every other key
 *                    becomes a Swiper breakpoint at that min-width. Falls
 *                    back to {0: 2, 768: 4} when omitted/invalid.
 * - data-page-label  Translated "Page" word for the fraction pagination
 * - data-of-label    Translated "of" word for the fraction pagination
 *
 * Expected markup:
 * <swiper-grid-carousel-latori>
 *   <div class="swiper">
 *     <div class="swiper-wrapper">
 *       <div class="swiper-slide">...</div>
 *     </div>
 *   </div>
 *   <div class="slider-nav">
 *     <button name="prev">...</button>
 *     <div class="swiper-pagination"></div>
 *     <button name="next">...</button>
 *   </div>
 * </swiper-grid-carousel-latori>
 */
class SwiperGridCarouselLatori extends HTMLElement {
  parseBreakpoints() {
    let breakpoints;
    try {
      breakpoints = JSON.parse(this.dataset.breakpoints || "null");
    } catch (error) {
      breakpoints = null;
    }
    if (
      !breakpoints ||
      typeof breakpoints !== "object" ||
      Object.keys(breakpoints).length === 0
    ) {
      breakpoints = { 0: 2, 768: 4 };
    }
    return breakpoints;
  }

  connectedCallback() {
    this.swiperEl = this.querySelector(".swiper");
    this.slides = this.swiperEl
      ? [...this.swiperEl.querySelectorAll(".swiper-slide")]
      : [];

    const rows = parseInt(this.dataset.gridRows, 10) || 1;
    const breakpoints = this.parseBreakpoints();
    // The smallest breakpoint's columns give the smallest page capacity -
    // comparing against the largest instead (as this used to) would mark a
    // count that still needs paging on mobile as globally inactive just
    // because it happens to fit on one page at the widest breakpoint.
    const minColumns = Math.min(...Object.values(breakpoints).map(Number));

    if (this.slides.length <= rows * minColumns) {
      this.setAttribute("inactive", "");
      return;
    }

    window.initLazyScript(this, this.init.bind(this));
  }

  init() {
    if (this.hasAttribute("loaded") || typeof Swiper === "undefined") return;

    const prevBtn = this.querySelector('button[name="prev"]');
    const nextBtn = this.querySelector('button[name="next"]');
    const paginationEl = this.querySelector(".swiper-pagination");
    const rows = parseInt(this.dataset.gridRows, 10) || 1;
    const pageLabel = this.dataset.pageLabel || "";
    const ofLabel = this.dataset.ofLabel || "/";

    const breakpoints = this.parseBreakpoints();
    const sortedWidths = Object.keys(breakpoints)
      .map(Number)
      .sort((a, b) => a - b);
    const baseWidth = sortedWidths[0];
    const baseColumns = Number(breakpoints[baseWidth]);

    const swiperBreakpoints = {};
    sortedWidths.slice(1).forEach((width) => {
      const columns = Number(breakpoints[width]);
      swiperBreakpoints[width] = {
        slidesPerView: columns,
        slidesPerGroup: columns,
        spaceBetween: 24,
      };
    });

    this.swiper = new Swiper(this.swiperEl, {
      slidesPerView: baseColumns,
      slidesPerGroup: baseColumns,
      spaceBetween: 12,
      grid: { rows, fill: "row" },
      watchOverflow: true,
      watchSlidesProgress: true,
      a11y: true,
      breakpoints: swiperBreakpoints,
      navigation:
        prevBtn && nextBtn ? { prevEl: prevBtn, nextEl: nextBtn } : false,
      pagination: paginationEl
        ? {
            el: paginationEl,
            type: "fraction",
            renderFraction: (currentClass, totalClass) =>
              `${pageLabel} <span class="${currentClass}"></span> ${ofLabel} <span class="${totalClass}"></span>`,
          }
        : false,
      on: {
        lock: () => this.setAttribute("inactive", ""),
        unlock: () => this.removeAttribute("inactive"),
      },
    });

    if (this.swiper.isLocked) this.setAttribute("inactive", "");

    this.setAttribute("loaded", "");
  }
}

customElements.define("swiper-grid-carousel-latori", SwiperGridCarouselLatori);
